/**
 * Skills Slice for Character Store
 *
 * Manages skill operations using string-based IDs exclusively.
 * Skills are stored with complete data (self-contained) including purchaseHistory.
 *
 * Requirements: 2.1, 2.2, 2.5, 3.1, 5.5
 */

import { StateCreator } from 'zustand';
import type { Character, Skill } from '../../../types/character';
import { SkillsService } from '../../../services/SkillsService';
import { ensureSkillPurchaseHistory } from '../../../utils/purchaseRecordHelpers';

/**
 * Skills slice interface defining all skill-related operations.
 * All operations use string IDs for skill identification.
 *
 * Requirements: 1.1, 2.1, 2.2, 2.5, 3.1, 5.5
 */
export interface SkillsSlice {
  /**
   * Add a new skill to the character.
   * Ensures purchaseHistory is initialized as empty array.
   *
   * @param skill - The skill to add (must include string id)
   *
   * Requirements: 2.5, 3.1, 5.5
   */
  addSkill: (skill: Skill) => void;

  /**
   * Update an existing skill by its string ID.
   *
   * @param skillId - The string ID of the skill to update
   * @param updates - Partial skill object with fields to update
   *
   * Requirements: 1.1, 2.5
   */
  updateSkill: (skillId: string, updates: Partial<Skill>) => void;

  /**
   * Remove a skill from the character by its string ID.
   *
   * @param skillId - The string ID of the skill to remove
   *
   * Requirements: 1.1
   */
  removeSkill: (skillId: string) => void;

  /**
   * Add a skill purchase to track advancement.
   * Creates the skill if it doesn't exist, or updates existing skill's purchaseHistory.
   * Automatically recalculates training level based on purchase history.
   *
   * @param skillId - The string ID of the skill (e.g., "dodge", "commerce")
   * @param advancementId - The unique ID of the advancement being purchased
   * @param rank - The career rank when this advancement was purchased
   * @param bonus - The bonus granted by this purchase (10 or 20)
   *
   * Requirements: 2.1, 2.2, 2.5, 3.1, 5.5
   */
  addSkillPurchase: (skillId: string, advancementId: string, rank: number, bonus: number) => void;

  /**
   * Remove a skill purchase from a character's skill.
   * Updates the training level based on remaining purchases.
   *
   * @param skillId - The string ID of the skill
   * @param advancementId - The advancement ID to remove
   *
   * Requirements: 3.1
   */
  removeSkillPurchase: (skillId: string, advancementId: string) => void;

  /**
   * Get a skill by its string ID from the character's skills.
   *
   * @param skillId - The string ID of the skill
   * @returns The skill or undefined if not found
   *
   * Requirements: 1.1, 2.1
   */
  getSkillById: (skillId: string) => Skill | undefined;

  /**
   * Check if a skill has a specific advancement purchase.
   *
   * @param skillId - The string ID of the skill
   * @param advancementId - The advancement ID to check for
   * @returns True if the skill has this advancement purchase
   *
   * Requirements: 3.1
   */
  hasSkillPurchase: (skillId: string, advancementId: string) => boolean;
}

// Helper interface to access the main character state
interface CharacterStore extends SkillsSlice {
  character: Character | null;
  hasUnsavedChanges: boolean;
}

export const createSkillsSlice: StateCreator<CharacterStore, [], [], SkillsSlice> = (set, get) => ({
  addSkill: (skill: Skill) => {
    const { character } = get();
    if (!character) return;

    // Ensure purchaseHistory is initialized for self-contained data
    const normalizedSkill = ensureSkillPurchaseHistory(skill);

    const updatedCharacter = {
      ...character,
      skills: [...character.skills, normalizedSkill],
      lastModified: new Date()
    };

    try {
      localStorage.setItem('rogue-trader-current-character', JSON.stringify(updatedCharacter));
    } catch (error) {
      console.warn('Failed to auto-save character to localStorage:', error);
    }

    set({
      character: updatedCharacter,
      hasUnsavedChanges: true
    });
  },

  updateSkill: (skillId: string, updates: Partial<Skill>) => {
    const { character } = get();
    if (!character) return;

    const updatedSkills = character.skills.map((skill) =>
      skill.id === skillId ? ensureSkillPurchaseHistory({ ...skill, ...updates }) : skill
    );

    set({
      character: {
        ...character,
        skills: updatedSkills,
        lastModified: new Date()
      },
      hasUnsavedChanges: true
    });
  },

  removeSkill: (skillId: string) => {
    const { character } = get();
    if (!character) return;

    set({
      character: {
        ...character,
        skills: character.skills.filter((skill) => skill.id !== skillId),
        lastModified: new Date()
      },
      hasUnsavedChanges: true
    });
  },

  addSkillPurchase: (skillId: string, advancementId: string, rank: number, bonus: number) => {
    const { character } = get();
    if (!character) return;

    // Use SkillsService to handle the purchase logic
    // This ensures consistent behavior and proper skill creation if needed
    const updatedSkills = SkillsService.addSkillPurchase(
      character.skills,
      skillId,
      advancementId,
      rank,
      bonus
    );

    const updatedCharacter = {
      ...character,
      skills: updatedSkills,
      lastModified: new Date()
    };

    try {
      localStorage.setItem('rogue-trader-current-character', JSON.stringify(updatedCharacter));
    } catch (error) {
      console.warn('Failed to auto-save character to localStorage:', error);
    }

    set({
      character: updatedCharacter,
      hasUnsavedChanges: true
    });
  },

  removeSkillPurchase: (skillId: string, advancementId: string) => {
    const { character } = get();
    if (!character) return;

    // Use SkillsService to handle the removal logic
    const updatedSkills = SkillsService.removeSkillPurchase(
      character.skills,
      skillId,
      advancementId
    );

    const updatedCharacter = {
      ...character,
      skills: updatedSkills,
      lastModified: new Date()
    };

    try {
      localStorage.setItem('rogue-trader-current-character', JSON.stringify(updatedCharacter));
    } catch (error) {
      console.warn('Failed to auto-save character to localStorage:', error);
    }

    set({
      character: updatedCharacter,
      hasUnsavedChanges: true
    });
  },

  getSkillById: (skillId: string) => {
    const { character } = get();
    if (!character) return undefined;

    return character.skills.find((skill) => skill.id === skillId);
  },

  hasSkillPurchase: (skillId: string, advancementId: string) => {
    const { character } = get();
    if (!character) return false;

    const skill = character.skills.find((s) => s.id === skillId);
    if (!skill) return false;

    return SkillsService.hasAdvancementPurchase(skill, advancementId);
  }
});
